import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translateLeagueBreadcrumb } from '@/i18n/sports-label';
import { stripMarketPlaceholders } from '@/i18n/market-labels';
import { sanitizeOutcomeDisplay } from '@/i18n/outcome-labels';
import { cn } from '@/lib/cn';
import { isFlagBadgeSrc } from '@/shell/sports-menu-utils';
import kitBlueSm from '@/assets/kits/kit-blue-sm.svg';
import kitWhiteSm from '@/assets/kits/kit-white-sm.svg';
import { Badge } from '../common/badge';
import { Icon } from '../common/icon';
import { DesktopOddsButton } from '../common/odds-button';
import { LiveClockText } from '../live/LiveClockText';
import type {
  EventCompetitor,
  EventOutcome,
  EventRowIndicator,
  EventRowProps,
} from './EventRow';

export interface DesktopEventMarket {
  code: string;
  outcomes: EventOutcome[];
}

export interface DesktopEventRowProps {
  league: string;
  category?: string;
  categoryId?: number;
  tournament?: string;
  tournamentId?: number;
  competitors: [EventCompetitor, EventCompetitor];
  markets: DesktopEventMarket[];
  type?: EventRowProps['type'];
  state?: EventRowProps['state'];
  /** Prematch date part, e.g. `10/08`. */
  kickoffDate?: string;
  /** Prematch time part, e.g. `21:15`. */
  kickoffTime?: string;
  clock?: string;
  liveClockState?: EventRowProps['liveClockState'];
  translatePeriod?: EventRowProps['translatePeriod'];
  crestSrc?: string;
  indicators?: EventRowIndicator[];
  additionalMarkets?: number;
  /** Prematch Bet Builder available (module + event flag already gated by caller). */
  betBuilderAvailable?: boolean;
  favorite?: boolean;
  onToggleFavorite?: () => void;
  onShowStats?: () => void;
  onSelectOutcome?: (outcome: EventOutcome) => void;
  onOpenEvent?: () => void;
  /** Opens the event in Bet Builder mode when the BB CTA is activated. */
  onOpenBetBuilder?: () => void;
  /** Opens live event details with the stream panel (live rows only). */
  onOpenLiveStream?: () => void;
  className?: string;
}

const KIT_SRC = [kitWhiteSm, kitBlueSm] as const;
const MARKET_GAP_PX = 8;
/** Gap between the lead chrome (teams/icons) and the markets slot. */
const LEAD_MARKETS_GAP_PX = 8;
/** Desktop odds chip width — sized so labels like `under 2.5` stay on one line. */
const OUTCOME_BTN_PX = 72;
const OUTCOME_GAP_PX = 4;

export function marketGroupWidth(market: DesktopEventMarket): number {
  const count = Math.max(market.outcomes.length, 1);
  return count * OUTCOME_BTN_PX + (count - 1) * OUTCOME_GAP_PX;
}

/** How many complete market groups fit in `availablePx` (including gaps). */
export function fittingMarketCount(
  markets: DesktopEventMarket[],
  availablePx: number,
): number {
  if (markets.length === 0) return 0;
  if (availablePx <= 0) return 1;

  let used = 0;
  let count = 0;
  for (const market of markets) {
    const width = marketGroupWidth(market);
    const needed = count === 0 ? width : used + MARKET_GAP_PX + width;
    if (needed > availablePx) break;
    used = needed;
    count += 1;
  }

  // Prefer at least one market; the slot clips if the row is extremely narrow.
  return count > 0 ? count : 1;
}

/**
 * Desktop full-width event row with odds on the right.
 * Prefer over {@link EventRow} on desktop Main (lg+).
 *
 * Fits market groups by measuring row width minus lead chrome (ResizeObserver),
 * so columns never spill outside the card.
 *
 * Market names sit in the header row, same column widths as the odds groups
 * below, so each label is centered on its market.
 */
export function DesktopEventRow({
  league,
  category,
  categoryId,
  tournament,
  tournamentId,
  competitors,
  markets,
  type = 'prematch',
  state = 'default',
  kickoffDate,
  kickoffTime,
  clock,
  liveClockState,
  translatePeriod,
  crestSrc,
  indicators,
  additionalMarkets,
  betBuilderAvailable = false,
  favorite = false,
  onToggleFavorite,
  onShowStats,
  onSelectOutcome,
  onOpenEvent,
  onOpenBetBuilder,
  onOpenLiveStream,
  className,
}: DesktopEventRowProps) {
  const { t, i18n } = useTranslation();
  const rowRef = useRef<HTMLElement>(null);
  const leadRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(1);

  const suspended = state === 'suspended';
  const isLive = type === 'live';
  const showLivestream = indicators?.includes('livestream') ?? false;
  const versusLabel = t('versus', { defaultValue: 'versus' });
  const liveStreamLabel = t('LiveStream', { defaultValue: 'Live stream available' });
  const leagueLabel =
    category || tournament
      ? translateLeagueBreadcrumb(
          i18n,
          t,
          { category, categoryId, tournament, tournamentId },
          ' · ',
        ) || league
      : league;

  // Fitting depends only on outcome counts, so odds updates don't re-measure.
  const marketsWidthKey = markets.map((market) => market.outcomes.length).join(',');
  const marketsRef = useRef(markets);
  useLayoutEffect(() => {
    marketsRef.current = markets;
  });

  useLayoutEffect(() => {
    const row = rowRef.current;
    const lead = leadRef.current;
    if (!row || !lead) return;

    let lastWidth = -1;
    const update = () => {
      const rowWidth = row.clientWidth;
      if (rowWidth === lastWidth) return;
      lastWidth = rowWidth;
      // Measure the card content box minus lead chrome — not the markets slot,
      // which grows with its children and would always "fit" every market.
      const available = rowWidth - lead.offsetWidth - LEAD_MARKETS_GAP_PX;
      setVisibleCount(fittingMarketCount(marketsRef.current, available));
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(row);
    return () => observer.disconnect();
  }, [marketsWidthKey]);

  const visibleMarkets = markets.slice(0, visibleCount);

  return (
    <article
      ref={rowRef}
      className={cn(
        'grid w-full min-w-0 max-w-full grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1 overflow-hidden rounded-md border border-border-event bg-bg-surface p-2',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-2 self-center overflow-clip">
        <button
          type="button"
          onClick={onOpenEvent}
          className="flex w-44 shrink-0 items-center gap-2 overflow-hidden text-left"
          title={leagueLabel}
        >
          {crestSrc ? (
            <span
              className={cn(
                'flex size-[18px] shrink-0 items-center justify-center overflow-clip rounded-full',
                !isFlagBadgeSrc(crestSrc) && 'bg-white',
              )}
            >
              <img
                src={crestSrc}
                alt=""
                className={
                  isFlagBadgeSrc(crestSrc) ? 'size-full object-cover' : 'size-3 object-contain'
                }
              />
            </span>
          ) : null}
          <span className="min-w-0 flex-1 truncate text-[11px] leading-[14px] tracking-wide text-text-secondary">
            {leagueLabel}
          </span>
        </button>

        {showLivestream || betBuilderAvailable ? (
          <span className="flex h-4 shrink-0 items-center gap-1.5 text-text-primary">
            {showLivestream ? (
              isLive && onOpenLiveStream ? (
                <button
                  type="button"
                  onClick={onOpenLiveStream}
                  className="inline-flex h-4 items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                  aria-label={liveStreamLabel}
                >
                  <Icon name="monitor-play" size={16} />
                </button>
              ) : (
                <Icon name="monitor-play" size={16} label={liveStreamLabel} />
              )
            ) : null}
            {betBuilderAvailable ? (
              <button
                type="button"
                onClick={onOpenBetBuilder ?? onOpenEvent}
                className="inline-flex h-4 items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                aria-label={t('BetBuilder', { defaultValue: 'Bet Builder' })}
              >
                <Icon name="betbuilder" size={16} />
              </button>
            ) : null}
          </span>
        ) : null}

        {additionalMarkets !== undefined && additionalMarkets > 0 ? (
          <button
            type="button"
            onClick={onOpenEvent}
            className="ml-auto shrink-0 text-[11px] leading-[14px] text-text-secondary hover:text-text-primary"
            aria-label={`${additionalMarkets} ${t('MoreMarkets', { defaultValue: 'more markets' })}`}
          >
            +{additionalMarkets}
          </button>
        ) : null}
      </div>

      <div className="flex min-w-0 items-center justify-end self-center overflow-hidden">
        <div className="flex w-max items-center gap-2">
          {visibleMarkets.map((market) => {
            const marketName = stripMarketPlaceholders(market.code);
            return (
              <div
                key={`label-${market.code}`}
                className="shrink-0 truncate text-center text-[11px] leading-[14px] text-text-secondary"
                style={{ width: marketGroupWidth(market) }}
                title={marketName}
              >
                {marketName}
              </div>
            );
          })}
        </div>
      </div>

      <div ref={leadRef} className="flex min-w-0 shrink-0 items-center gap-2 self-center">
        {isLive ? (
          <div className="flex w-[52px] shrink-0 flex-col items-center justify-center gap-0.5 pr-2 text-center text-text-secondary">
            <Badge tone="live">{t('Live', { defaultValue: 'Live' })}</Badge>
            {clock || liveClockState ? (
              <LiveClockText
                liveClockState={liveClockState}
                clock={clock}
                translatePeriod={translatePeriod}
                layout="stacked"
                className="text-[11px] leading-[14px]"
              />
            ) : null}
          </div>
        ) : (
          <div className="flex w-12 shrink-0 flex-col items-center justify-center gap-0.5 pr-2 text-center text-text-secondary">
            {kickoffDate ? (
              <span className="text-[11px] leading-[14px]">{kickoffDate}</span>
            ) : null}
            {kickoffTime ? (
              <span className="text-xs font-bold leading-[15px]">{kickoffTime}</span>
            ) : null}
          </div>
        )}

        <button
          type="button"
          onClick={onOpenEvent}
          className="flex w-44 shrink-0 flex-col gap-1 overflow-hidden text-left"
          aria-label={`${competitors[0].name} ${versusLabel} ${competitors[1].name}`}
        >
          {competitors.map((competitor, index) => (
            <span key={`${competitor.name}-${index}`} className="flex w-full items-center gap-1.5">
              <span className="relative size-[18px] shrink-0 overflow-clip">
                <img src={KIT_SRC[index]} alt="" className="size-full object-contain" />
              </span>
              <span className="min-w-0 flex-1 truncate text-[13px] leading-4 font-bold text-text-primary">
                {competitor.name}
              </span>
              {competitor.serving ? (
                <span
                  className="size-1.5 shrink-0 rounded-full bg-status-warning"
                  title={t('Serving', { defaultValue: 'Serving' })}
                  aria-label={t('Serving', { defaultValue: 'Serving' })}
                />
              ) : null}
              {competitor.setScore !== undefined ? (
                <span className="shrink-0 text-[11px] tabular-nums text-text-secondary/70">
                  {competitor.setScore}
                </span>
              ) : null}
              {competitor.periodScores ? (
                <span className="shrink-0 text-[11px] tabular-nums text-text-secondary">
                  {competitor.periodScores}
                </span>
              ) : null}
              {competitor.score !== undefined ? (
                <span className="shrink-0 text-[13px] font-bold tabular-nums text-text-primary">
                  {competitor.score}
                </span>
              ) : null}
            </span>
          ))}
        </button>

        <div className="h-10 w-px shrink-0 bg-border-subtle" aria-hidden />

        <div className="flex shrink-0 flex-col items-center gap-1">
          <button
            type="button"
            onClick={onToggleFavorite}
            aria-label={
              favorite
                ? t('RemoveFromFavourites', { defaultValue: 'Remove from favourites' })
                : t('AddToFavourites', { defaultValue: 'Add to favourites' })
            }
            aria-pressed={favorite}
            className={cn(
              'flex size-4 items-center justify-center text-icon-muted hover:text-icon-default',
              favorite && 'text-action-primary hover:text-action-primary',
            )}
          >
            <Icon
              name="star"
              size={14}
              {...(favorite ? { fill: 'currentColor', stroke: 'none' } : {})}
            />
          </button>
          {onShowStats ? (
            <button
              type="button"
              onClick={onShowStats}
              aria-label={t('MatchStatistics', { defaultValue: 'Match statistics' })}
              className="flex size-4 items-center justify-center text-icon-muted hover:text-icon-default"
            >
              <Icon name="stats" size={14} />
            </button>
          ) : null}
        </div>
      </div>

      <div className="flex min-w-0 items-center justify-end self-center overflow-hidden">
        <div className="flex w-max items-center gap-2">
          {visibleMarkets.map((market) => (
            <div
              key={market.code}
              className="flex h-11 shrink-0 items-center gap-1"
              style={{ width: marketGroupWidth(market) }}
            >
              {market.outcomes.map((outcome) => (
                <DesktopOddsButton
                  key={outcome.id}
                  outcome={sanitizeOutcomeDisplay(outcome.outcome)}
                  odds={outcome.odds}
                  state={suspended ? 'locked' : (outcome.state ?? 'default')}
                  flash={outcome.flash ?? 'none'}
                  onClick={() => onSelectOutcome?.(outcome)}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}
